// ============= Imports =============
import { WorkspaceRelayHubEvents } from './events';
import type {
  WorkspaceRelayEventHandler,
  WorkspaceUploadEventDetails,
  WorkspaceUploadEventPayload,
} from './types';

// ============= Toast name resolution =============

export interface ToastNameResolvers {
  getDisplayNameForUser?: (userId: string, workspaceId?: string) => string;
  getWorkspaceNameById?: (workspaceId: string) => string;
}

/**
 * Resolves userId and workspaceId to user-friendly names for toast messages.
 * Uses optional resolvers from context; falls back to the raw id when no resolver or not found.
 */
export function resolveToastNames(
  resolvers: ToastNameResolvers,
  opts: { userId?: string; workspaceId?: string }
): { userName: string; workspaceName: string } {
  const { userId, workspaceId } = opts;
  const userName =
    userId != null
      ? (resolvers.getDisplayNameForUser?.(userId, workspaceId) ?? userId)
      : 'Unknown';
  const workspaceName =
    workspaceId != null
      ? (resolvers.getWorkspaceNameById?.(workspaceId) ?? workspaceId)
      : 'Unknown';
  return { userName, workspaceName };
}

// ============= Types =============

export interface WorkspaceEventHandlerContext {
  workspaceId?: string;
  log: (event: string, data: unknown) => void;
  onEventReceived?: (
    event: string,
    data: unknown,
    parsed?: Record<string, unknown>,
  ) => void;
  onMessageReceived?: (message: unknown) => void;
  onDataRefresh?: () => void | Promise<void>;
  enableToasts: boolean;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  /** Optional: resolve userId to display name for toasts. */
  getDisplayNameForUser?: (userId: string, workspaceId?: string) => string;
  /** Optional: resolve workspaceId to workspace title for toasts. */
  getWorkspaceNameById?: (workspaceId: string) => string;
}

export interface WorkspaceEventConfig {
  event: WorkspaceRelayHubEvents;
  getParsed: (
    param1: unknown,
    workspaceIdParam: string,
    userMessage: unknown,
  ) => Record<string, unknown>;
  toastType: 'success' | 'error' | 'info';
  getToastMessage: (parsed: Record<string, unknown>) => string;
  includeOnMessageReceived?: boolean;
  includeDataRefresh?: boolean;
}

const FILE_NAME_PATTERN = /File:\s*([^.]+\.\w+)/i;

export const isWorkspaceUploadEventPayload = (
  payload: unknown,
): payload is WorkspaceUploadEventPayload =>
  typeof payload === 'object' && payload !== null;

export const getWorkspaceUploadEventPayload = (
  payload: unknown,
): WorkspaceUploadEventPayload | undefined =>
  isWorkspaceUploadEventPayload(payload) ? payload : undefined;

export const getWorkspaceUploadEventMessage = (
  payload: unknown,
): string | undefined => {
  if (typeof payload === 'string') {
    return payload;
  }

  const eventPayload = getWorkspaceUploadEventPayload(payload);
  return (
    eventPayload?.message ||
    eventPayload?.error ||
    eventPayload?.fileName ||
    eventPayload?.name
  );
};

export const getWorkspaceUploadEventFileName = (
  message?: string,
  payload?: WorkspaceUploadEventPayload | string,
): string => {
  const payloadObject =
    typeof payload === 'string' ? undefined : payload;
  const messageMatch = message?.match(FILE_NAME_PATTERN);

  return (
    payloadObject?.fileName ||
    payloadObject?.name ||
    payloadObject?.file?.name ||
    messageMatch?.[1] ||
    'Document'
  );
};

export const normalizeWorkspaceUploadEvent = (
  param1: unknown,
  workspaceIdParam?: unknown,
  userMessage?: unknown,
): WorkspaceUploadEventDetails => {
  let actualWorkspaceId: string;
  let actualMessage: string | undefined;
  let actualUserMessage: WorkspaceUploadEventPayload | string | undefined;

  if (userMessage === undefined && typeof workspaceIdParam === 'string') {
    actualWorkspaceId =
      typeof param1 === 'string'
        ? param1
        : getWorkspaceUploadEventPayload(param1)?.workspaceId || 'unknown';
    actualMessage = workspaceIdParam;
    actualUserMessage = { message: workspaceIdParam };
  } else {
    actualWorkspaceId =
      typeof workspaceIdParam === 'string'
        ? workspaceIdParam
        : typeof param1 === 'string'
          ? param1
          : getWorkspaceUploadEventPayload(param1)?.workspaceId || 'unknown';
    actualUserMessage =
      typeof userMessage === 'string'
        ? userMessage
        : getWorkspaceUploadEventPayload(userMessage);
    actualMessage = getWorkspaceUploadEventMessage(userMessage);
  }

  const errorText =
    (typeof actualUserMessage === 'string' && actualUserMessage) ||
    (typeof actualUserMessage === 'object' && actualUserMessage?.error) ||
    (typeof actualUserMessage === 'object' && actualUserMessage?.message) ||
    actualMessage ||
    undefined;

  return {
    actualWorkspaceId,
    actualMessage,
    actualUserMessage,
    fileName: getWorkspaceUploadEventFileName(actualMessage, actualUserMessage),
    errorText,
  };
};

// ============= Reusable Handler Factory =============

/**
 * Creates a handler for workspace events that use the (param1, workspaceIdParam, userMessage) signature.
 * Reduces duplication across file, workflow, collection, and ask session handlers.
 */
export const createWorkspaceEventHandler = (
  ctx: WorkspaceEventHandlerContext,
  config: WorkspaceEventConfig,
): WorkspaceRelayEventHandler => {
  const {
    event,
    getParsed,
    toastType,
    getToastMessage,
    includeOnMessageReceived = false,
    includeDataRefresh = false,
  } = config;

  return (param1, workspaceIdParam, userMessage) => {
    if (ctx.workspaceId && workspaceIdParam !== ctx.workspaceId) {
      return;
    }

    const parsed = getParsed(param1, workspaceIdParam ?? '', userMessage);

    ctx.log(event, { param1, workspaceId: workspaceIdParam, userMessage, parsed });

    if (ctx.onEventReceived) {
      ctx.onEventReceived(
        event,
        { param1, workspaceId: workspaceIdParam, userMessage },
        parsed
      );
    }

    if (ctx.enableToasts) {
      const message = getToastMessage(parsed);
      if (toastType === 'success') ctx.showSuccess(message);
      else if (toastType === 'error') ctx.showError(message);
      else ctx.showInfo(message);
    }

    if (includeDataRefresh && ctx.onDataRefresh) {
      void ctx.onDataRefresh();
    }

    if (includeOnMessageReceived && ctx.onMessageReceived) {
      ctx.onMessageReceived({
        event,
        workspaceId: workspaceIdParam || param1 || 'unknown',
        userMessage,
        parsed,
      });
    }
  };
}
